import type { ServiceLocation } from "@/modules/shared/serviceLocation";

/**
 * The price a service row states on the clinic details view (H59), where no
 * location has been chosen yet (docs/ux/screens/BusinessPublicProfileScreen.md,
 * Services).
 *
 * **One figure when one figure is true.** A clinic that offers one location,
 * or charges the same at both, has one price, and labelling it would invent a
 * distinction. Two different totals are both stated, each with its place,
 * because "from $40" would hide the one a home visit costs.
 *
 * Ported to both apps number for number (`ServicePrice.swift`,
 * `ServicePrice.kt`): the order is In-Clinic then At-Home, the labels are
 * lower case, and the separator is " · ".
 */
const ORDER: readonly ServiceLocation[] = ["in_clinic", "at_home"];

const LABEL: Record<ServiceLocation, string> = {
  in_clinic: "in clinic",
  at_home: "at home",
};

export function servicePriceLine(
  priceByLocation: Partial<Record<ServiceLocation, string>>,
  offered: readonly ServiceLocation[],
  basePrice: string,
): string {
  const prices = ORDER.filter((location) => offered.includes(location))
    .map((location) => ({ location, price: priceByLocation[location] }))
    .filter((entry): entry is { location: ServiceLocation; price: string } =>
      Boolean(entry.price),
    );
  if (prices.length === 0) return basePrice;
  if (prices.every((entry) => entry.price === prices[0].price)) return prices[0].price;
  return prices.map((entry) => `${entry.price} ${LABEL[entry.location]}`).join(" · ");
}
